import type { ReactNode } from 'react';

/**
 * Pill — VERBATIM port from ~/FrontendFinal/Design/src/components/Pill.tsx.
 *
 * The design's most repeated primitive: a 32px-high rounded box (r8) holding
 * 13px/300 text with 12px horizontal padding. Fill is the only axis that varies,
 * over three values: surface (#FEFEFE), accent-subtle (teal @ 0.1),
 * accent-strong (teal @ 0.6). See the Design repo's header for full provenance.
 *
 * `as="button"` is the ONE addition to the port. The Learning page's level lens
 * and topic filter are pill rows you can press, and the design draws them as
 * pills — so rather than hand-rolling a second pill look, the primitive grows an
 * element seam. The default (`as="span"`) renders byte-identical markup to the
 * port, so every existing call site (StepPills, the wizard headers) is untouched;
 * the button branch only adds a pointer cursor and the focus ring Profile's own
 * toggle uses (`inset 0 0 0 2px var(--ink)` — see .pp-collection-toggle__btn).
 */

export type PillTone = 'surface' | 'subtle' | 'strong';

const TONE: Record<PillTone, string> = {
  surface: 'bg-surface text-text',
  subtle: 'bg-accent-subtle text-text',
  // On the 0.6 fill the label switches to --ink at full opacity.
  strong: 'bg-accent-strong text-text-on-accent',
};

/** The shape both branches share — the port's own class list. */
const BASE = [
  'inline-flex h-pill items-center gap-5 rounded-md px-pill-x',
  'text-sm font-light whitespace-nowrap',
];

export interface PillProps {
  tone?: PillTone;
  children: ReactNode;
  /** Leading icon, e.g. the magnifier in the search field. */
  icon?: ReactNode;
  className?: string;
  'aria-current'?: 'step' | 'page' | 'true' | undefined;
  /**
   * Element to render. `'span'` (default) is the design's decorative pill;
   * `'button'` makes it a real control — pass `onClick` and the pressed state.
   */
  as?: 'span' | 'button';
  /** `as="button"` only. */
  onClick?: () => void;
  /** `as="button"` only — a toggle's on/off state for assistive technology. */
  'aria-pressed'?: boolean;
  /** `as="button"` only — when the visible label alone is not the whole story. */
  'aria-label'?: string;
}

export function Pill({
  tone = 'surface',
  children,
  icon,
  className = '',
  'aria-current': ariaCurrent,
  as = 'span',
  onClick,
  'aria-pressed': ariaPressed,
  'aria-label': ariaLabel,
}: PillProps) {
  if (as === 'button') {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-current={ariaCurrent}
        aria-pressed={ariaPressed}
        aria-label={ariaLabel}
        className={[
          ...BASE,
          'cursor-pointer transition-colors duration-micro',
          'focus-visible:outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--ink)]',
          TONE[tone],
          className,
        ].join(' ')}
      >
        {icon}
        {children}
      </button>
    );
  }

  return (
    <span aria-current={ariaCurrent} className={[...BASE, TONE[tone], className].join(' ')}>
      {icon}
      {children}
    </span>
  );
}
